/* ============================================================
   PublicBuy Theme. Global JavaScript
   Cart Drawer, Mobile Nav, Carousels, Reveal Animations
   ============================================================ */

'use strict';

/* ── CART STATE ───────────────────────────────────────────── */
const PBCart = {
  drawer:    null,
  overlay:   null,
  countEls:  [],
  cart:      null,
  pendingQty: {},
  qtySyncTimers: {},

  init() {
    this.drawer  = document.getElementById('pb-cart-drawer');
    this.overlay = document.getElementById('pb-cart-overlay');
    this.countEls = document.querySelectorAll('.pb-nav__cart-count');

    // Open triggers
    document.querySelectorAll('[data-cart-open]').forEach(el =>
      el.addEventListener('click', () => this.open())
    );
    // Close triggers
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    document.querySelectorAll('[data-cart-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    // Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    this.refresh();
  },

  open() {
    this.drawer?.classList.add('open');
    this.overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  },

  close() {
    this.drawer?.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.style.overflow = '';
  },

  async refresh() {
    try {
      const res  = await fetch('/cart.js');
      const cart = this.applyPendingToCart(await res.json());
      this.cart = cart;
      this.updateCount(cart.item_count);
      this.renderItems(cart);
      this.syncCardControls(cart);
    } catch(e) {
      console.warn('Cart refresh failed', e);
    }
  },

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.classList.toggle('visible', count > 0);
    });
  },

  renderItems(cart) {
    const body       = document.getElementById('pb-cart-body');
    const subtotalEl = document.getElementById('pb-cart-subtotal');
    const countEl    = document.getElementById('pb-cart-item-count');
    const savingsPill= document.getElementById('pb-cart-savings-pill');
    const savingsVal = document.getElementById('pb-cart-savings-val');
    const progressEl = document.getElementById('pb-cart-progress');
    const progressBar= document.getElementById('pb-cart-progress-fill');
    const progressTxt= document.getElementById('pb-cart-progress-text');if (!body) return;

    const FREE_SHIP_THRESHOLD = 49900; // ₹499 in paise

    // Update total
    if (subtotalEl) subtotalEl.textContent = PBCart.formatMoney(cart.total_price);

    // Update item count label
    if (countEl) countEl.textContent = cart.item_count === 1 ? '1 item' : `${cart.item_count} items`;

    // Savings pill, sum up all discounts
    let totalSavings = 0;
    cart.items.forEach(item => {
      if (item.compare_at_price && item.compare_at_price > item.price) {
        totalSavings += (item.compare_at_price - item.price) * item.quantity;
      }
    });
    if (savingsPill && savingsVal) {
      if (totalSavings > 0) {
        savingsVal.textContent = PBCart.formatMoney(totalSavings);
        savingsPill.style.display = 'flex';
      } else {
        savingsPill.style.display = 'none';
      }
    }

    // Free shipping — always free, no threshold
    if (progressEl && progressBar && progressTxt) {
      progressBar.style.width = '100%';
      progressTxt.innerHTML = '<strong style="color:#4ade80">🎉 Free shipping on this order!</strong>';
      progressEl.style.background = 'transparent';
    }

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="pb-cart-drawer__empty">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1.5">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          <p style="font-weight:700;color:#fff">Your cart is empty</p>
          <p style="font-size:13px;color:rgba(255,255,255,0.4)">Add products to get started</p>
          <a href="/collections/all" class="btn btn--primary btn--sm" onclick="PBCart.close()">Shop Now</a>
        </div>`;
      return;
    }

    body.innerHTML = cart.items.map(item => {
      const variantStr = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
      const imgSrc = item.image
        ? (item.image.startsWith('//') ? 'https:' + item.image : item.image).replace(/(\.\w+)(\?|$)/, '_120x120$1$2')
        : '';
      return `
      <div class="pb-cart-item" data-key="${item.key}">
        <img class="pb-cart-item__img" src="${imgSrc}" alt="${item.product_title}" loading="lazy">
        <div class="pb-cart-item__info">
          <div class="pb-cart-item__vendor">${item.vendor || ''}</div>
          <div class="pb-cart-item__name">${item.product_title}</div>
          ${variantStr ? `<div class="pb-cart-item__variant">${variantStr}</div>` : ''}
          <div class="pb-cart-item__bottom">
            <div>
              <span class="pb-cart-item__price">${PBCart.formatMoney(item.final_line_price)}</span>
            </div>
            <div class="pb-cart-item__qty">
              <button onclick="PBCart.changeQty('${item.key}', ${item.quantity - 1})" aria-label="Decrease">&#8722;</button>
              <span>${item.quantity}</span>
              <button onclick="PBCart.changeQty('${item.key}', ${item.quantity + 1})" aria-label="Increase">&#43;</button>
            </div>
          </div>
          <span class="pb-cart-item__remove" onclick="PBCart.changeQty('${item.key}', 0)">Remove</span>
        </div>
      </div>`;
    }).join('');
  },

  async addItem(variantId, quantity = 1, btn = null) {
    let added = false;
    const slot = btn?.closest('.pb-product-card__add-slot[data-variant-id]') || null;
    const previousMarkup = slot ? slot.innerHTML : '';
    if (btn) {
      btn.disabled = true;
    }
    if (slot) {
      this.renderQtyControl(slot, { key: `pending-${variantId}`, quantity });
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) throw new Error('Add failed');
      added = true;
      await this.refresh();
      PBFlash.show('Added to cart');
    } catch(e) {
      PBFlash.show('Could not add to cart', 'error');
      if (slot) slot.innerHTML = previousMarkup;
      if (btn) btn.disabled = false;
    } finally {
      if (btn && !added) btn.disabled = false;
    }
  },

  async changeQty(key, qty) {
    const cart = this.cart;
    this.pendingQty[key] = qty;
    if (cart) {
      const item = cart.items.find(cartItem => cartItem.key === key);
      if (item) {
        item.quantity = qty;
        if (qty <= 0) {
          cart.items = cart.items.filter(cartItem => cartItem.key !== key);
        }
        cart.item_count = cart.items.reduce((sum, cartItem) => sum + cartItem.quantity, 0);
        cart.total_price = cart.items.reduce((sum, cartItem) => sum + (cartItem.final_price * cartItem.quantity), 0);
        this.updateCount(cart.item_count);
        this.renderItems(cart);
        this.syncCardControls(cart);
      }
    }
    this.queueQtySync(key);
  },

  formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  },

  syncCardControls(cart) {
    document.querySelectorAll('.pb-product-card__add-slot[data-variant-id]').forEach(slot => {
      const variantId = Number(slot.dataset.variantId);
      const item = cart.items.find(cartItem =>
        Number(cartItem.variant_id) === variantId || Number(cartItem.id) === variantId
      );

      if (!item || item.quantity < 1) {
        this.renderAddControl(slot, variantId);
        return;
      }

      this.renderQtyControl(slot, item);
    });
  },

  renderAddControl(slot, variantId) {
    slot.innerHTML = `
      <button class="pb-product-card__add-btn" type="button"
        onclick="event.preventDefault(); event.stopPropagation(); PBCart.addItem('${variantId}', 1, this)">
        ADD
      </button>`;
  },

  renderQtyControl(slot, item) {
    const decreaseTo = Math.max((item.quantity || 1) - 1, 0);
    const increaseTo = (item.quantity || 0) + 1;
    slot.innerHTML = `
      <div class="pb-product-card__qty-control" aria-label="Cart quantity selector">
        <button class="pb-product-card__qty-btn" type="button"
          aria-label="Decrease quantity"
          onclick="event.preventDefault(); event.stopPropagation(); PBCart.changeQty('${item.key}', ${decreaseTo})">
          <span>&#8722;</span>
        </button>
        <span class="pb-product-card__qty-count">${item.quantity}</span>
        <button class="pb-product-card__qty-btn" type="button"
          aria-label="Increase quantity"
          onclick="event.preventDefault(); event.stopPropagation(); PBCart.changeQty('${item.key}', ${increaseTo})">
          <span>&#43;</span>
        </button>
      </div>`;
  },

  queueQtySync(key) {
    clearTimeout(this.qtySyncTimers[key]);
    this.qtySyncTimers[key] = setTimeout(() => {
      this.flushQtySync(key);
    }, 320);
  },

  async flushQtySync(key) {
    const qty = this.pendingQty[key];
    if (typeof qty === 'undefined') return;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });

      if (this.pendingQty[key] !== qty) {
        this.queueQtySync(key);
        return;
      }

      delete this.pendingQty[key];
      delete this.qtySyncTimers[key];
      await this.refresh();
    } catch (e) {
      console.warn('Qty change failed', e);
      delete this.pendingQty[key];
      delete this.qtySyncTimers[key];
      await this.refresh();
    }
  },

  applyPendingToCart(cart) {
    const pendingEntries = Object.entries(this.pendingQty);
    if (!pendingEntries.length) return cart;

    const nextCart = {
      ...cart,
      items: cart.items.map(item => ({ ...item }))
    };

    pendingEntries.forEach(([key, qty]) => {
      const item = nextCart.items.find(cartItem => cartItem.key === key);
      if (item) item.quantity = qty;
    });

    nextCart.items = nextCart.items.filter(item => item.quantity > 0);
    nextCart.item_count = nextCart.items.reduce((sum, item) => sum + item.quantity, 0);
    nextCart.total_price = nextCart.items.reduce((sum, item) => sum + (item.final_price * item.quantity), 0);

    return nextCart;
  }
};

/* ── FLASH NOTIFICATION ───────────────────────────────────── */
const PBFlash = {
  el: null,
  timer: null,

  init() {
    this.el = document.getElementById('pb-flash');
  },

  show(msg, type = 'success') {
    if (!this.el) return;
    this.el.querySelector('.pb-flash__msg').textContent = msg;
    this.el.querySelector('.pb-flash__dot').style.background =
      type === 'error' ? 'var(--pink)' : 'var(--green)';
    this.el.classList.add('show');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.el.classList.remove('show'), 2800);
  }
};

/* ── MOBILE NAV ───────────────────────────────────────────── */
const PBNav = {
  init() {
    const hamburger = document.getElementById('pb-hamburger');
    const mobileNav = document.getElementById('pb-mobile-nav');
    const overlay   = document.getElementById('pb-mobile-overlay');
    const closeBtn  = document.getElementById('pb-mobile-close');
    if (!hamburger || !mobileNav) return;

    const openNav = () => {
      mobileNav.classList.add('open');
      mobileNav.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('open');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const spans = hamburger.querySelectorAll('span');
      spans[0].style.transform = 'rotate(45deg) translate(5px, 5px)';
      spans[1].style.opacity   = '0';
      spans[2].style.transform = 'rotate(-45deg) translate(5px, -5px)';
    };

    const closeNav = () => {
      mobileNav.classList.remove('open');
      mobileNav.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      const spans = hamburger.querySelectorAll('span');
      spans.forEach(s => { s.style.transform = ''; s.style.opacity = ''; });
    };

    hamburger.addEventListener('click', () => {
      mobileNav.classList.contains('open') ? closeNav() : openNav();
    });

    // Close button inside drawer
    if (closeBtn) closeBtn.addEventListener('click', closeNav);

    // Close on overlay click
    if (overlay) overlay.addEventListener('click', closeNav);

    // Close on link click
    mobileNav.querySelectorAll('a').forEach(a =>
      a.addEventListener('click', closeNav)
    );

    // Close on Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && mobileNav.classList.contains('open')) closeNav();
    });
  }
};

/* ── CAROUSELS ────────────────────────────────────────────── */
const PBCarousel = {
  init() {
    document.querySelectorAll('.pb-carousel-wrap').forEach(wrap => {
      const track = wrap.querySelector('.pb-carousel');
      const prev  = wrap.querySelector('.pb-carousel-btn--prev');
      const next  = wrap.querySelector('.pb-carousel-btn--next');
      if (!track) return;

      const scrollBy = () => track.querySelector('.pb-product-card')?.offsetWidth + 20 || 260;

      prev?.addEventListener('click', () =>
        track.scrollBy({ left: -scrollBy(), behavior: 'smooth' })
      );
      next?.addEventListener('click', () =>
        track.scrollBy({ left: scrollBy(), behavior: 'smooth' })
      );
    });
  }
};

/* ── REVEAL ON SCROLL ─────────────────────────────────────── */
const PBReveal = {
  init() {
    const observer = new IntersectionObserver(entries => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('visible'),
            (e.target.dataset.delay || 0) * 80);
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });

    document.querySelectorAll('.pb-reveal').forEach((el, i) => {
      el.dataset.delay = el.dataset.delay || i % 6;
      observer.observe(el);
    });
  }
};

/* ── VARIANT SELECTOR (Product Page) ─────────────────────── */
const PBVariants = {
  init() {
    const form = document.getElementById('pb-product-form');
    if (!form) return;

    const variantData  = JSON.parse(document.getElementById('pb-variant-data')?.textContent || '[]');
    const addBtn       = form.querySelector('[data-add-btn]');
    const priceEl      = document.getElementById('pb-product-price');
    const comparePriceEl = document.getElementById('pb-product-compare-price');
    const variantIdInput = form.querySelector('input[name="id"]');

    form.querySelectorAll('.pb-variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        // Deselect siblings
        btn.closest('.pb-option-group').querySelectorAll('.pb-variant-btn')
          .forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Find matching variant
        const selected = {};
        form.querySelectorAll('.pb-option-group').forEach(group => {
          const active = group.querySelector('.pb-variant-btn.active');
          if (active) selected[group.dataset.option] = active.dataset.value;
        });

        const variant = variantData.find(v =>
          v.options.every((opt, i) => opt === selected[`option${i+1}`])
        );

        if (variant) {
          variantIdInput.value = variant.id;
          if (priceEl) priceEl.textContent = PBCart.formatMoney(variant.price);
          if (comparePriceEl) {
            comparePriceEl.textContent = variant.compare_at_price > variant.price
              ? PBCart.formatMoney(variant.compare_at_price) : '';
          }
          if (addBtn) {
            addBtn.disabled = !variant.available;
            addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
          }
          // Update URL without reload
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      });
    });

    // Add to cart
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const id  = form.querySelector('input[name="id"]').value;
      const qty = parseInt(form.querySelector('input[name="quantity"]')?.value || 1);
      await PBCart.addItem(id, qty, addBtn);
    });
  }
};

/* ── IMAGE GALLERY (Product Page) ────────────────────────── */
const PBGallery = {
  init() {
    const thumbs = document.querySelectorAll('.pb-gallery__thumb');
    const main   = document.getElementById('pb-gallery-main');
    if (!thumbs.length || !main) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        main.src = thumb.dataset.full;
        main.alt = thumb.alt;
      });
    });
  }
};

/* ── QUANTITY INPUT ───────────────────────────────────────── */
const PBQty = {
  init() {
    document.querySelectorAll('.pb-qty-wrap').forEach(wrap => {
      const input = wrap.querySelector('input');
      wrap.querySelector('[data-minus]')?.addEventListener('click', () => {
        if (parseInt(input.value) > 1) input.value = parseInt(input.value) - 1;
      });
      wrap.querySelector('[data-plus]')?.addEventListener('click', () => {
        input.value = parseInt(input.value) + 1;
      });
    });
  }
};

/* ── COLLECTION FILTER ────────────────────────────────────── */
const PBFilter = {
  init() {
    document.querySelectorAll('[data-sort]').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', btn.dataset.sort);
        window.location = url;
      });
    });
  }
};


/* ══════════════════════════════════════════════════════════════
   PBPrefill. Collect customer info across the journey,
   autofill Shopify checkout via URL parameters.
   Storage key: pb_prefill  (localStorage JSON object)
   Fields: email, zip, city, first_name, last_name, phone
   ══════════════════════════════════════════════════════════════ */
const PBPrefill = {

  STORE_KEY: 'pb_prefill',

  // Read entire store
  all() {
    try { return JSON.parse(localStorage.getItem(this.STORE_KEY) || '{}'); }
    catch(e) { return {}; }
  },

  // Get one field
  get(key) { return this.all()[key] || ''; },

  // Set one field
  set(key, val) {
    const data = this.all();
    data[key] = val;
    try { localStorage.setItem(this.STORE_KEY, JSON.stringify(data)); } catch(e) {}
  },

  // Build Shopify prefill checkout URL
  // Base: /cart (redirects to checkout with cart contents)
  buildCheckoutURL() {
    const d = this.all();
    const base = '/checkout';
    const params = new URLSearchParams();
    if (d.email)      params.set('checkout[email]', d.email);
    if (d.first_name) params.set('checkout[shipping_address][first_name]', d.first_name);
    if (d.last_name)  params.set('checkout[shipping_address][last_name]',  d.last_name);
    if (d.zip)        params.set('checkout[shipping_address][zip]',         d.zip);
    if (d.city)       params.set('checkout[shipping_address][city]',        d.city);
    if (d.phone)      params.set('checkout[shipping_address][phone]',       d.phone);
    // Always India
    params.set('checkout[shipping_address][country]', 'India');
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },

  // ── STEP 1: Email widget on product page ──────────────────
  initEmailWidget() {
    const wrap   = document.getElementById('pb-prefill-email-wrap');
    if (!wrap) return;
    const form   = document.getElementById('pb-prefill-email-form');
    const input  = document.getElementById('pb-prefill-email-input');
    const btn    = document.getElementById('pb-prefill-email-btn');
    const saved  = document.getElementById('pb-prefill-email-saved');
    const valEl  = document.getElementById('pb-prefill-email-val');

    // If already saved, show saved state
    const existing = this.get('email');
    if (existing) {
      if (form) form.style.display = 'none';
      if (saved) saved.style.display = 'flex';
      if (valEl) valEl.textContent = existing;
    }

    if (!btn || !input) return;
    const save = () => {
      const email = input.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        input.style.borderBottom = '1px solid var(--pink)';
        input.placeholder = 'Please enter a valid email';
        return;
      }
      PBPrefill.set('email', email);
      if (form)  form.style.display  = 'none';
      if (saved) saved.style.display = 'flex';
      if (valEl) valEl.textContent   = email;
    };
    btn.addEventListener('click', save);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') save(); });
  },

  // ── STEP 2: Pincode widget in cart drawer ────────────────
  initDrawerPin() {
    const input  = document.getElementById('pb-drawer-pin-input');
    const btn    = document.getElementById('pb-drawer-pin-btn');
    const result = document.getElementById('pb-drawer-pin-result');
    if (!input || !btn) return;

    // Pre-fill if already stored
    const existing = this.get('zip');
    if (existing) {
      input.value = existing;
      if (result) {
        result.textContent = '✓ Saved · ' + existing;
        result.className = 'pb-cart-drawer__pin-result success';
      }
    }

    const check = () => {
      const pin = input.value.trim();
      if (!pin || pin.length !== 6 || !/^\d+$/.test(pin)) {
        if (result) { result.textContent = 'Enter a valid 6-digit pincode'; result.className = 'pb-cart-drawer__pin-result error'; }
        return;
      }
      if (result) { result.textContent = 'Checking…'; result.className = 'pb-cart-drawer__pin-result'; }
      setTimeout(() => {
        // Save zip; city approximation stored too
        PBPrefill.set('zip', pin);
        PBPrefill.set('city', 'India'); // city resolved at checkout; Shopify maps from zip
        if (result) {
          result.textContent = '✓ Delivers to ' + pin + ' · 3–5 days';
          result.className = 'pb-cart-drawer__pin-result success';
        }
      }, 600);
    };
    btn.addEventListener('click', check);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') check(); });
  },

  // ── STEP 3: Name + phone on cart page ────────────────────
  initCartFields() {
    const firstEl = document.getElementById('pb-prefill-first');
    const lastEl  = document.getElementById('pb-prefill-last');
    const phoneEl = document.getElementById('pb-prefill-phone');
    const badge   = document.getElementById('pb-cart-prefill-badge');
    if (!firstEl) return;

    // Pre-fill saved values
    if (this.get('first_name')) { firstEl.value = this.get('first_name'); firstEl.classList.add('pb-prefill-filled'); }
    if (this.get('last_name'))  { lastEl.value  = this.get('last_name');  lastEl.classList.add('pb-prefill-filled'); }
    if (this.get('phone'))      { phoneEl.value = this.get('phone');      phoneEl.classList.add('pb-prefill-filled'); }

    const showBadge = () => {
      if (badge && (this.get('first_name') || this.get('phone'))) {
        badge.style.display = 'inline-flex';
      }
    };
    showBadge();

    const saveField = (el, key) => {
      el.addEventListener('blur', () => {
        const v = el.value.trim();
        if (v) {
          PBPrefill.set(key, v);
          el.classList.add('pb-prefill-filled');
          showBadge();
        }
      });
    };
    saveField(firstEl, 'first_name');
    saveField(lastEl,  'last_name');
    saveField(phoneEl, 'phone');
  },

  // ── STEP 4: Intercept ALL checkout buttons ────────────────
  initCheckoutIntercept() {
    // Drawer checkout button, navigate to cart page
    const drawerBtn = document.getElementById('pb-drawer-checkout-btn');
    if (drawerBtn) {
      drawerBtn.addEventListener('click', e => {
        e.preventDefault();
        window.location.href = '/cart';
      });
    }

    // Cart page checkout button (submit → intercept)
    const cartForm = document.getElementById('pb-cart-form');
    const cartBtn  = cartForm?.querySelector('.pb-cart-page__checkout-btn');
    if (cartBtn && cartForm) {
      cartBtn.addEventListener('click', async e => {
        e.preventDefault();
        // Save any unsaved field values immediately before redirect
        const f = document.getElementById('pb-prefill-first');
        const l = document.getElementById('pb-prefill-last');
        const p = document.getElementById('pb-prefill-phone');
        if (f?.value.trim()) PBPrefill.set('first_name', f.value.trim());
        if (l?.value.trim()) PBPrefill.set('last_name',  l.value.trim());
        if (p?.value.trim()) PBPrefill.set('phone',      p.value.trim());

        // FIX #1: Submit qty updates to cart BEFORE redirecting to checkout.
        // Without this, any quantity changes the user made on the cart page
        // were never sent to Shopify, they'd always checkout with qty=1.
        try {
          const updates = {};
          cartForm.querySelectorAll('input[name="updates[]"]').forEach(input => {
            const key = input.id.replace('qty-', '');
            updates[key] = parseInt(input.value) || 1;
          });
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        } catch (err) {
          console.warn('PBPrefill: cart update failed, proceeding anyway', err);
        }

        window.location.href = PBPrefill.buildCheckoutURL();
      });
    }
  },

  init() {
    this.initEmailWidget();
    this.initDrawerPin();
    this.initCartFields();
    this.initCheckoutIntercept();
  }
};

const PBBrandCards = {
  brands: {
    poco: 'POCO',
    google: 'Google',
    oneplus: 'OnePlus',
    samsung: 'Samsung',
    oppo: 'OPPO',
    cmf: 'CMF',
    vivo: 'Vivo',
    realme: 'Realme',
    iqoo: 'iQOO',
    redmi: 'Redmi',
    motorola: 'Motorola'
  },

  norm(value) {
    return (value || '').trim().toLowerCase();
  },

  brandKey(card) {
    return [...card.classList].map(className => {
      const match = className.match(/^pbb__card--(.+)$/);
      return match ? match[1] : '';
    }).find(key => this.brands[key]) || '';
  },

  setCount(card, count) {
    let countEl = card.querySelector('[data-brand-count]');
    if (!countEl) {
      countEl = document.createElement('span');
      countEl.className = 'pbb__count';
      countEl.setAttribute('data-brand-count', '');
      card.querySelector('.pbb__head')?.appendChild(countEl);
    }
    if (countEl) countEl.textContent = `${count} phone${count === 1 ? '' : 's'}`;
  },

  async fetchAllProducts(page = 1, acc = []) {
    const res = await fetch(`/collections/all/products.json?limit=250&page=${page}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`pb-brand-products-${res.status}`);
    const data = await res.json();
    const products = Array.isArray(data?.products) ? data.products : [];
    acc.push(...products);
    return products.length === 250 ? this.fetchAllProducts(page + 1, acc) : acc;
  },

  async init() {
    const cards = [...document.querySelectorAll('.pb-brands .pbb__card')];
    if (!cards.length) return;

    cards.forEach(card => {
      const key = this.brandKey(card);
      if (!key) return;
      const query = this.brands[key];
      const href = `/collections/all?brand_title=${encodeURIComponent(this.norm(query))}`;
      card.setAttribute('href', href);
      card.setAttribute('data-brand-query', query);
      card.setAttribute('data-brand-href', href);
    });

    try {
      const products = await this.fetchAllProducts();
      cards.forEach(card => {
        const query = this.norm(card.getAttribute('data-brand-query'));
        if (!query) return;
        const count = products.reduce((total, product) => {
          return this.norm(product?.title).includes(query) ? total + 1 : total;
        }, 0);
        this.setCount(card, count);
      });
    } catch (err) {
      console.warn('PBBrandCards: count refresh failed', err);
    }
  }
};

/* ── BOOT ─────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  PBCart.init();
  PBFlash.init();
  PBNav.init();
  PBCarousel.init();
  PBReveal.init();
  PBVariants.init();
  PBGallery.init();
  PBQty.init();
  PBFilter.init();
  PBPrefill.init();
  PBBrandCards.init();
});
